import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles } from '@/mocks/articles';
import { faqsPt, faqsEn, faqsDe } from '@/mocks/faqs';
import { applySeo, applyNoIndex, FAVICON_URL, type SeoData } from '@/lib/seo';
import {
  clearSchemas,
  injectOrganizationSchema,
  injectWebSiteSchema,
  injectArticleSchema,
  injectBreadcrumbSchema,
  injectFaqSchema,
} from '@/lib/schema';

const HOME_SEO: SeoData = {
  title: 'CSSBuy Spreadsheet | Product Lists, Guides & Resources',
  description:
    'Explore CSSBuy spreadsheets, product resources, seller guides, QC tips, shipping information and practical buying resources in one organized hub.',
  keywords:
    'CSSBuy Spreadsheet, CSSBuy products, CSSBuy guide, CSSBuy shipping, CSSBuy review, buying agent, replica spreadsheet',
  path: '/',
  ogType: 'website',
  image: FAVICON_URL,
};

const PRODUCTS_SEO: SeoData = {
  title: 'CSSBuy Spreadsheet | Product Catalog',
  description:
    'Browse curated CSSBuy products across shoes, hoodies, t-shirts, jackets, bags, accessories and more. Click any item to view details and purchase on the original platform.',
  keywords:
    'CSSBuy products, CSSBuy spreadsheets, sneakers, streetwear, bags, accessories, buying agent',
  path: '/products',
  ogType: 'website',
  image: FAVICON_URL,
};

const FAQ_SEO: SeoData = {
  title: 'CSSBuy Spreadsheet — FAQ',
  description:
    'Find answers to common questions about CSSBuy spreadsheets, QC photos, shipping costs, coupons, warehouses, seller verification and safety.',
  keywords:
    'CSSBuy FAQ, CSSBuy spreadsheet, CSSBuy shipping, CSSBuy QC, CSSBuy safety, buying agent',
  path: '/faq',
  ogType: 'website',
  image: FAVICON_URL,
};

export default function SeoManager() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const lang = i18n.language;

  useEffect(() => {
    const path = location.pathname;

    // Clean up any JSON-LD injected for a previous route so it cannot leak.
    clearSchemas();

    if (path === '/') {
      applySeo(HOME_SEO);
      injectOrganizationSchema();
      injectWebSiteSchema();
      return;
    }

    if (path === '/products') {
      applySeo(PRODUCTS_SEO);
      injectWebSiteSchema();
      injectBreadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Products', path: '/products' },
      ]);
      return;
    }

    if (path === '/faq') {
      applySeo(FAQ_SEO);
      const faqData = lang === 'pt' ? faqsPt : lang === 'en' ? faqsEn : faqsDe;
      injectFaqSchema(faqData);
      injectBreadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'FAQ', path: '/faq' },
      ]);
      return;
    }

    if (path.startsWith('/article/')) {
      const slug = path.replace('/article/', '');
      const article = articles.find((a) => a.slug === slug);
      if (article) {
        const title =
          lang === 'pt' ? article.title : lang === 'en' ? article.titleEn : article.titleDe;
        const description =
          lang === 'pt'
            ? article.description
            : lang === 'en'
              ? article.descriptionEn
              : article.descriptionDe;

        applySeo({
          title,
          description,
          keywords:
            'CSSBuy Spreadsheet, CSSBuy guide, buying agent, replica spreadsheet, QC, shipping',
          path: `/article/${article.slug}`,
          ogType: 'article',
          image: article.image,
        });
        injectArticleSchema(article, lang, title, description);
        injectBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: title, path: `/article/${article.slug}` },
        ]);
        return;
      }
    }

    // Unknown / not-found route: keep it out of the index.
    applyNoIndex();
    document.title = 'Page Not Found';
  }, [location.pathname, lang]);

  return null;
}